import React, { useCallback, Profiler } from 'react';
import { useParams } from 'react-router-dom';
import { useProjectViewStates } from './ProjectViewStates';
import { ProjectViewContent } from './ProjectViewContent';
import { ReportActionData } from '../ReportActions';
import { getCommandPaletteTabOptions, createGraphBuilderTab } from '../../tab-system/TabRegistry';
import logger from '../../utils/logger';
import { TabOption } from '../TabCreationComboBox';
import { ReportElementUnion } from '../../../types/report.types';
import { PaneNode } from '../../../types/pane.types';
import { createReportElementId } from '../../../types/id.types';
import { useProject } from '../../hooks/useProject';
import { SaveFileModal } from '../SaveFileModal';

// カスタムフックのインポート
import { useProjectInitialization } from './hooks/useProjectInitialization';
import { useDatasetOperations } from './hooks/useDatasetOperations';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useReportOperations } from './hooks/useReportOperations';
import { useGlobalFunctions } from './hooks/useGlobalFunctions';
import { useTabDragAndDrop } from './hooks/useTabDragAndDrop';

interface ProjectViewProps {
  projectId?: string;
}

/**
 * ProjectViewInner - タブシステムを使用したプロジェクト表示コンポーネント
 *
 * リファクタリング後：
 * - ロジックを複数のカスタムフックに分離
 * - メインコンポーネントは統合と表示のみを担当
 * - 各機能が独立したモジュールとして管理される
 */
function ProjectViewInner({ projectId: propProjectId }: ProjectViewProps) {
  // React RouterのパラメータからprojectIdを取得（優先）
  const params = useParams<{ projectId: string }>();
  const projectId = propProjectId || params.projectId || '';

  // プロジェクトを読み込む
  const { loading: projectLoading, error: projectError } = useProject(projectId);

  // 基本的な状態とロジック
  const states = useProjectViewStates(projectId);
  const {
    isLoading,
    error,
    project,
    tabCreationComboBoxRef,
    layout,
    activateTab,
    updateLayout,
    handleDropZone,
    reportManager,
    persistenceManager,
    tabOperations,
    startDragOverlay,
    endDragOverlay,
    isDragging,
    dragState,
    closeTab,
    startDrag,
    endDrag,
    moveTabToPane
  } = states;

  // エイリアス
  const handleOpenDataset = tabOperations.openDatasetTab;
  const handleOpenReport = tabOperations.openReportTab;
  const openTabWithModifier = tabOperations.openTabWithModifier;

  // updateLayout関数をメモ化
  const updateLayoutCallback = useCallback((newLayout: { rootPane: PaneNode | null; activeContainerId: string }) => {
    updateLayout((currentLayout) => {
      // rootPaneがnullの場合は現在のレイアウトを維持
      if (!newLayout.rootPane) {
        return currentLayout;
      }
      return {
        rootPane: newLayout.rootPane,
        activeContainerId: newLayout.activeContainerId || currentLayout.activeContainerId
      };
    });
  }, [updateLayout]);

  // プロジェクト初期化
  useProjectInitialization({
    project,
    isLoading,
    layout: {
      rootPane: layout.rootPane,
      activeContainerId: layout.activeContainerId || ''
    },
    updateLayout: updateLayoutCallback,
    activateTab
  });

  // データセット操作
  useDatasetOperations({
    project,
    projectId,
    updateProjectState: async (project) => {
      await persistenceManager.updateProjectState(project);
    },
    openDatasetTab: handleOpenDataset
  });

  // キーボードショートカット
  useKeyboardShortcuts({
    tabCreationComboBoxRef,
    layout: {
      rootPane: layout.rootPane,
      activeContainerId: layout.activeContainerId || ''
    },
    closeTab,
    activateTab
  });

  // レポート操作
  const {
    handleAddToReportGeneric,
    handleUpdateReport,
    handleExportReport,
    handleDuplicateReport,
    handleCreateNewReportGeneric
  } = useReportOperations({
    project,
    reportManager,
    updateProjectState: async (project) => {
      await persistenceManager.updateProjectState(project);
    },
    openReportTab: handleOpenReport
  });

  // グローバル関数
  useGlobalFunctions({
    layout: {
      rootPane: layout.rootPane,
      activeContainerId: layout.activeContainerId || ''
    },
    activateTab
  });

  // handleTabDropの実装
  const handleTabDrop = useCallback((draggedTabId: string, targetContainerId: string, dropIndex?: number) => {
    // タブを含むコンテナを探す
    const findContainerWithTab = (node: PaneNode | null, tabId: string): string | null => {
      if (node?.type === 'container') {
        if (node.tabs?.some((tab) => tab.id === tabId)) {
          return node.id;
        }
      } else if (node?.type === 'split') {
        const children = [node.leftChild, node.rightChild].filter(Boolean);
        for (const child of children) {
          const containerId = findContainerWithTab(child, tabId);
          if (containerId) return containerId;
        }
      }
      return null;
    };
    
    const fromContainerId = findContainerWithTab(layout.rootPane, draggedTabId);
    if (fromContainerId) {
      moveTabToPane(draggedTabId, fromContainerId, targetContainerId, dropIndex);
    }
  }, [moveTabToPane, layout.rootPane]);

  // ドラッグ&ドロップ
  const dragDropHandlers = useTabDragAndDrop({
    startDrag,
    endDrag,
    handleDropZone,
    startDragOverlay: (tabId: string, containerId: string, _dragElement: HTMLElement) => {
      startDragOverlay(tabId, containerId, layout.rootPane);
    },
    endDragOverlay
  });

  // Web版では他ウィンドウの変更監視は不要
  React.useEffect(() => {
    // Web版では他ウィンドウとの同期は不要
  }, []);

  // タブ作成オプションを生成
  const getTabCreationOptions = useCallback((): TabOption[] => {
    // TabRegistryから基本オプションを取得（Graph Builder以外）
    const registryOptions = getCommandPaletteTabOptions()
      .filter(option => option.id !== 'graph-builder')
      .map(option => ({
        ...option,
        action: async (event?: React.MouseEvent | React.KeyboardEvent) => {
          if (!layout.rootPane) return;
          const tab = option.action();
          openTabWithModifier(tab, event);
        }
      }));
    
    // Graph Builderオプション（サブメニュー付き）
    const graphBuilderOptions: TabOption[] = [
      {
        id: 'graph-builder',
        label: 'Graph Builder',
        icon: '📊',
        description: 'Create graphs and visualizations',
        action: async (event?: React.MouseEvent | React.KeyboardEvent) => {
          if (!layout.rootPane) return;
          const tab = createGraphBuilderTab();
          openTabWithModifier(tab, event);
        }
      }
    ];

    // カスタムオプション（データセットとレポート）
    const options: TabOption[] = [
      ...registryOptions,
      ...graphBuilderOptions,
      {
        id: 'new-report',
        label: 'New Empty Report',
        icon: '📄',
        description: 'Create a new empty report',
        action: async () => {
          try {
            const newReport = await reportManager.createEmptyReport('New Report', '');
            if (newReport) {
              handleOpenReport(newReport.id);
            }
          } catch (error) {
            logger.error('[ProjectView] Failed to create new report:', error);
          }
        }
      }
    ];

    // データセット用のオプションを追加
    if (project?.datasets) {
      Object.entries(project.datasets).forEach(([datasetId, dataset]) => {
        options.push({
          id: `dataset-${datasetId}`,
          label: dataset.name,
          icon: '📋',
          description: 'Dataset view',
          action: (event) => handleOpenDataset(datasetId, event)
        });
      });
    }

    // レポート用のオプションを追加
    if (project?.reports && project.reports.length > 0) {
      project.reports.forEach((report) => {
        options.push({
          id: `report-${report.id}`,
          label: report.name || 'Untitled Report',  // nameがなければデフォルト値
          icon: '📈',
          description: 'Report view',
          action: (event) => handleOpenReport(report.id, event)
        });
      });
    }

    return options;
  }, [project?.datasets, project?.reports, layout.rootPane, openTabWithModifier, handleOpenReport, handleOpenDataset, reportManager]);

  // レポートコンテキスト値
  const reportContextValue = {
    availableReports: project?.reports || [],
    onAddToReport: handleAddToReportGeneric,
    onCreateNewReport: handleCreateNewReportGeneric,
    onCreateReport: handleCreateNewReportGeneric,
    onAddToExistingReport: handleAddToReportGeneric,
    onCreateReportAndOpen: async (data: ReportActionData, _reportName?: string) => {
      await handleCreateNewReportGeneric(data);
    },
    selectedReportId: reportManager.selectedReportId,
    onSelectReport: (reportId: string | null) => {
      if (reportId) {
        reportManager.selectReport(reportId);
      }
    },
    onDeleteReport: async (reportId: string) => {
      await reportManager.deleteReport(reportId);
    },
    onUpdateReport: handleUpdateReport,
    onUpdateReportElement: async (reportId: string, elementId: string, updates: Partial<ReportElementUnion>) => {
      await reportManager.updateReportElement(reportId, elementId, updates);
    },
    onDeleteReportElement: async (reportId: string, elementId: string) => {
      await reportManager.deleteReportElement(reportId, elementId);
    },
    onExportReport: handleExportReport,
    onDuplicateReport: handleDuplicateReport,
    onRenameReport: async (reportId: string, newName: string) => {
      await reportManager.updateReportName(reportId, newName);
    },
    onContentChange: async (reportId: string, content: string) => {
      await reportManager.updateContent(reportId, content);
    },
    onAddElement: async (reportId: string, elementType: string, elementData?: Record<string, unknown>) => {
      // 新しい要素IDを生成
      const elementId = createReportElementId(`${elementType}-${crypto.randomUUID()}`);
      // 要素を作成
      const element = {
        ...elementData,
        id: elementId,
        type: elementType,
      } as ReportElementUnion;
      await reportManager.addElement(reportId, element);
    }
  };

  // コンテンツコンポーネントに渡す
  return (
    <>
      <ProjectViewContent
        isLoading={isLoading || projectLoading}
        error={error || projectError}
        project={project}
        layout={layout}
        handleTabDragStart={dragDropHandlers.onTabDragStart}
        handleTabDragEnd={dragDropHandlers.onTabDragEnd}
        handleTabDrop={handleTabDrop}
        handleDropZone={dragDropHandlers.onDropZoneDrop}
        isDragging={isDragging}
        dragState={dragState}
        getTabCreationOptions={getTabCreationOptions}
        tabCreationComboBoxRef={tabCreationComboBoxRef}
        persistenceManager={{
          saveProjectToFile: persistenceManager.saveProjectToFile,
          saveProjectToFileAs: persistenceManager.saveProjectToFileAs
        }}
        reportContextValue={reportContextValue}
      />
      
      {/* ファイル保存モーダル */}
      {persistenceManager.isSaveModalOpen && persistenceManager.pendingProject && (
        <SaveFileModal
          isOpen={persistenceManager.isSaveModalOpen}
          onClose={persistenceManager.closeSaveModal}
          onSave={persistenceManager.handleSaveCallback}
          defaultFileName={persistenceManager.pendingProject.projectName}
          fileExtension=".mds"
          fileTypeName="MIDAS Project"
          title="Save Project"
        />
      )}
    </>
  );
}

/**
 * ProjectView - ProfilerでラップしたProjectViewInner
 *
 * プロファイリングログは環境変数 VITE_ENABLE_PROFILER=true で有効化できる
 */
export function ProjectView(props: ProjectViewProps) {
  const enableProfiler = import.meta.env.VITE_ENABLE_PROFILER === 'true';

  const onRenderCallback = (
    _id: string,
    phase: "mount" | "update" | "nested-update",
    actualDuration: number,
    _baseDuration: number,
    _startTime: number,
    _commitTime: number
  ) => {
    if (enableProfiler) {
      console.log(`[Profiler] ProjectView ${phase}: ${actualDuration.toFixed(2)}ms`);
    }
  };

  return (
    <Profiler id="ProjectView" onRender={onRenderCallback}>
      <ProjectViewInner {...props} />
    </Profiler>
  );
}